<template>
	<div
		:data-theme="route.params.resort"
		:class="{
      [reservationData.class ?? '']: true,
    }"
	>
		<PageHeader :title="reservationData.title" :back-to="backTo" />

		<UspBlock :usps="reservationData.usp_block ?? []" class="mx-4.5" />

		<DatePicker v-model="selectedDate" :workspace-id="workspaceId" :label="datePickerLabel" />

		<div class="py-5 px-4.5 flex flex-col gap-3" aria-live="polite" :aria-busy="loading">
			<template v-if="loading">
				<p class="sr-only" role="status">{{ t('loading') }}</p>
				<SkeletonLoadingCard type="reservation-option" />
			</template>

			<template v-if="!loading && reservationOptions && reservationOptions.length > 0">
				<ReservationOption
					v-if="baseProduct"
					:option="baseProduct"
					:selected-date
					:loading="loadingDetailId === baseProduct.id"
					@details="showDetails(baseProduct, $event)"
					@book="openReservationFlow"
				/>

				<h2
					v-if="baseProduct && otherOptions.length > 0"
					class="mt-2 font-serif font-normal text-base leading-8 text-center text-white"
				>
					{{ t('arrangements_and_deals') }}
				</h2>

				<ReservationOption
					v-for="option in otherOptions"
					:key="option.id"
					:option="option"
					:selected-date
					:loading="loadingDetailId === option.id"
					@details="showDetails(option, $event)"
					@book="openReservationFlow"
				/>

				<p v-if="reservationData.flow_type" class="text-sm text-right text-white/70">
					{{ t(reservationData.flow_type === 'hotel' ? 'prices_per_person_per_night' : 'prices_per_person') }}
				</p>
			</template>

			<div
				v-if="!loading && reservationOptions && reservationOptions.length === 0"
				class="flex items-center justify-center flex-1"
			>
				<p
					v-if="reservationData.no_products_description_html"
					class="text-lg text-center text-white/50 [&_a]:underline [&_a]:text-white/80"
					v-html="reservationData.no_products_description_html"
				/>
				<p v-else class="text-lg text-center text-white/50">{{ reservationData.no_products_description ?? '' }}</p>
			</div>
		</div>

		<ProductInfoDialog
			v-if="showPopup && popupBuyable"
			:selected-date="selectedDate"
			:buyable="popupBuyable"
			:choice="popupChoice"
			:choices="popupChoices"
			:initial-index="popupInitialIndex"
			@close="showPopup = false"
			@book="(id) => openReservationFlow(getReservationProductById(id))"
		/>
	</div>
</template>

<script lang="ts" setup>
import type {Buyable, BuyableChoice, BuyablesEndpoint, Workspace} from '../api/resources';
import {computed, onMounted, ref, watch} from 'vue';
import bridge from '@thermenresorts/bridge';
import { useApi } from '../api';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PageHeader from '../components/PageHeader.vue';
import ProductInfoDialog from '../components/ProductInfoDialog.vue';
import ReservationOption from '../components/ReservationOption.vue';
import useStore from '../store';
import DatePicker from "../components/DatePicker.vue";
import SkeletonLoadingCard from '../components/SkeletonLoadingCard.vue';
import UspBlock from '../components/UspBlock.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const api = useApi();

// #4774: Guard against an invalid/mis-embedded resort ref that would otherwise crash render.
if (!store.hasResort(route.params.resort as string)) {
  router.replace({ name: 'resort-choice' });
}

const selectedDate = ref<string>(route.params.date as string);

const backTo = computed(() => ({
  name: 'resort-reserve',
  params: {
    resort: route.params.resort,
    date: route.params.date,
    // Back to the sub level (e.g. workshops overview) when we navigated through one
    card_id: (route.query.parent_card as string) ?? undefined,
  },
}));

const workspace = ref<Workspace | undefined>(store.getResort(route.params.resort as string))
const workspaceId = computed(() => workspace.value?.id);
const popupBuyable = ref<Buyable>();
const popupChoice = ref<BuyableChoice>();
const popupChoices = ref<BuyableChoice[]>();
const popupInitialIndex = ref(0);
const showPopup = ref(false);

const loading = ref(true);
const loadingDetailId = ref(null);

const reservationOptions = ref<Buyable[]>([]);

const reservationData = ref<BuyablesEndpoint>({
  title: '',
  description: '',
  class: '',
  image: 'Hamamruimte_vriendinnen.webp',
  no_products_description: '',
  flow_type: null,
  usp_block: [],
  data: []
});

// The base product is locked to the top; the other options follow the "Arrangementen & deals" header
const baseProduct = computed(() => reservationOptions.value.find((option) => option.is_base_product));
const otherOptions = computed(() => reservationOptions.value.filter((option) => !option.is_base_product));

const datePickerLabel = computed(() => {
  switch (reservationData.value.flow_type) {
    case 'entree':
    case 'workshop':
    case 'workshops_overview':
      return t('choose_date');
    case 'hotel':
    case 'retreat':
      return t('choose_arrival_date');
    default:
      return undefined;
  }
});

const showDetails = async (option, choice) => {
  // Product with multiple options (e.g. Entree = hele dag/avond/ochtend): show them as a
  // carousel straight from the already loaded choices, no extra API call needed.
  if (option.choices && option.choices.length > 0) {
    popupBuyable.value = option;
    popupChoice.value = undefined;
    popupChoices.value = option.choices;
    // Always start at the first option (e.g. hele dag), then browse to the others
    popupInitialIndex.value = 0;
    showPopup.value = true;
    return;
  }

  // Standalone product: fetch the full product info as before.
  loadingDetailId.value = choice.id;

  try {
    const response = await api.product.get(
      choice.id,
      choice.entity,
      selectedDate.value === 'none' ? undefined : selectedDate.value as string
    );
    popupBuyable.value = option;
    // Prefer the long description in the pop-up, matching the bundle-choice behaviour in Buyables.
    popupChoice.value = {
      ...response.data,
      description: response.data.long_description || response.data.description,
    };
    popupChoices.value = undefined;
    popupInitialIndex.value = 0;
    showPopup.value = true;
  } catch (error) {
    console.log(error);
  }
  loadingDetailId.value = null;
};

onMounted(() => {
  getReservationOptions();
});

watch(() => selectedDate.value, () => {
  loading.value = true;
  getReservationOptions();
})

const getBuyablesCall = computed(() => {
  const resort = store.getResort(route.params.resort as string);
  if (!resort) {
    return undefined;
  }

  return selectedDate.value === 'none'
    ? api.workspaces.getBuyables(resort.id, route.params.productset_id as string, store.state.globalFilters)
    : api.workspaces.getBuyablesForDate(resort.id, route.params.productset_id as string, selectedDate.value as string, store.state.globalFilters);
});

const getReservationOptions = async () => {
  const buyablesCall = getBuyablesCall.value;
  if (!buyablesCall) {
    loading.value = false;
    return;
  }

  try {
    const response = await buyablesCall;
    reservationData.value = response.data;
    reservationOptions.value = response.data.data;
  } catch (error) {
    console.log(error);
  }

  loading.value = false;
};

const getReservationProductById = (id: number) =>
  popupBuyable.value && popupBuyable.value.choices && popupBuyable.value.choices.length > 0
    ? popupBuyable.value.choices.find(po => po.id === id)
    : popupBuyable.value;

const openReservationFlow = (product) => {
  showPopup.value = false;
  let resort = route.params.resort;

  // #1300: Should send thermen resorts if we chose the resorts.
  if(window.location.host.includes('thermenresorts')) {
    resort = 'resorts';
  }

  bridge.sendMessage('pre-reservation-flow', 'reservation-flow.open', {
    url: product.reservation_flow_url,
    ga_origin: `Preflow | thermen ${resort}`,
  });
};
</script>
